import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, MessageCircle, Star, Check, X } from 'lucide-react';
import { database } from '../supabase';
// import { fetchQuizData } from '../firebase-setup'; // Backup - keeping dormant
import './Shared-Quiz.css';
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, handleRedirectResult } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';

const SharedQuiz = () => {
    const { uid, quizId } = useParams();
    const [quizData, setQuizData] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showExplanations, setShowExplanations] = useState({});
    const [answerFeedback, setAnswerFeedback] = useState({});
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [score, setScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const navigate = useNavigate();
    const { user, authInitialized } = useAuth();
    
    const signUpModal = () => {
        setShowSignUpModal(true);
    };

    // Helper function to clean text and remove asterisks
    const cleanText = (text) => {
        if (!text) return '';
        return text.replace(/\*+/g, '').trim();
    };

    // Helper function to shuffle array
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Helper function to process quiz data (same as TakeQuiz)
    const processQuizData = useCallback((data) => {
        // Handle both Firebase and Supabase data structures
        const questions = data.questions || data.quiz_data;
        if (!data || !questions) return data;

        const processedQuestions = questions.map(question => {
            // Clean question text
            const cleanQuestion = cleanText(question.question);
            
            // Clean and shuffle options (use 'options' not 'answers')
            const cleanOptions = (question.options || question.answers || []).map(option => cleanText(option));
            const shuffledOptions = shuffleArray(cleanOptions);
            
            // Find the correct answer index after shuffling
            const originalCorrectAnswer = cleanText((question.options || question.answers || [])[question.correct_answer || 0]);
            const newCorrectIndex = shuffledOptions.findIndex(option => option === originalCorrectAnswer);
            
            console.log('Debug - Original question:', question);
            console.log('Debug - Clean options:', cleanOptions);
            console.log('Debug - Shuffled options:', shuffledOptions);
            console.log('Debug - Original correct answer:', originalCorrectAnswer);
            console.log('Debug - New correct index:', newCorrectIndex);

            return {
                ...question,
                id: question.id || `q_${Math.random().toString(36).substr(2, 9)}`,
                question: cleanQuestion,
                options: shuffledOptions,
                correct_answer: newCorrectIndex >= 0 ? newCorrectIndex : 0,
                original_correct_answer: originalCorrectAnswer
            };
        });

        return {
            ...data,
            questions: processedQuestions
        };
    }, []);  

    // Handle redirect result on page load
    useEffect(() => {
        const handleAuthRedirect = async () => {
            if (authInitialized) {
                const redirectResult = await handleRedirectResult();
                if (redirectResult.success && redirectResult.result) {
                    console.log('Redirect authentication successful');
                    navigate(`/main/${redirectResult.result.user.uid}`);
                }
            }
        };

        handleAuthRedirect();
    }, [authInitialized, navigate]);

    // Don't auto-navigate existing users - let them stay on quiz page

    const handleSignInWithGoogle = async () => {
        setAuthLoading(true);
        setAuthError(null);
        
        try {
            const result = await signInWithGoogle();
            
            if (result.method === 'popup' && result.result) {
                // For popup method, navigate immediately
                navigate(`/main/${result.result.user.uid}`);
                setShowSignUpModal(false);
            }
            // For redirect method, navigation will happen automatically
        } catch (error) {
            console.error('Authentication error:', error);
            setAuthError(error.message);
        } finally {
            setAuthLoading(false);
        }
    };

    useEffect(() => {
        const loadQuizData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                console.log('Fetching quiz data for:', { uid, quizId });
                
                // Fetch quiz data from Supabase
                const rawQuizData = await database.getQuizByFirebaseUid(uid, quizId);
                console.log('Raw quiz data loaded:', rawQuizData);
                
                // Process the quiz data to handle both Firebase and Supabase structures
                const processedQuizData = processQuizData(rawQuizData);
                console.log('Processed quiz data:', processedQuizData);
                
                setQuizData(processedQuizData);
                setLoading(false);
            } catch (err) {
                console.error('Error loading quiz data:', err);
                setError(err.message || 'Failed to load quiz data');
                setLoading(false);
            }
        };

        if (uid && quizId) {
            loadQuizData();
        } else {
            setError('Invalid quiz URL parameters');
            setLoading(false);
        }
    }, [uid, quizId, processQuizData]);

    const handleAnswerSelect = (questionId, answerIndex) => {
        // Don't allow changing answers once selected
        if (selectedAnswers[questionId] !== undefined) {
            return;
        }

        const currentQuestion = quizData.questions.find(q => q.id === questionId);
        console.log('Debug - Question:', currentQuestion);
        console.log('Debug - Selected answer index:', answerIndex);
        console.log('Debug - Correct answer index:', currentQuestion.correct_answer);
        console.log('Debug - Is correct:', answerIndex === currentQuestion.correct_answer);
        
        const isCorrect = answerIndex === currentQuestion.correct_answer;
        
        setSelectedAnswers(prev => ({
            ...prev,
            [questionId]: answerIndex
        }));
        
        // Set feedback for this answer
        setAnswerFeedback(prev => ({
            ...prev,
            [questionId]: {
                selected: answerIndex,
                correct: currentQuestion.correct_answer,
                isCorrect: isCorrect
            }
        }));
        
        // Show explanation when answer is selected
        setShowExplanations(prev => ({
            ...prev,
            [questionId]: true
        }));

        // Update score
        if (isCorrect) {
            setScore(prev => prev + 1);
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < quizData.questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            // Quiz completed
            setQuizCompleted(true);
        }
    };

    const handlePrevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const getGradeMessage = (score, total) => {
        const percentage = (score / total) * 100;
        
        if (percentage >= 90) {
            return {
                message: "Excellent! Outstanding performance!",
                color: "#10b981",
                emoji: "🎉"
            };
        } else if (percentage >= 80) {
            return {
                message: "Great job! Well done!",
                color: "#3b82f6",
                emoji: "👏"
            };
        } else if (percentage >= 70) {
            return {
                message: "Good work! Keep it up!",
                color: "#f59e0b",
                emoji: "👍"
            };
        } else if (percentage >= 60) {
            return {
                message: "Not bad! Room for improvement.",
                color: "#f97316",
                emoji: "📚"
            };
        } else {
            return {
                message: "Keep studying! You can do better!",
                color: "#ef4444",
                emoji: "💪"
            };
        }
    };

    if (loading) {
        return (
            <div className="shared-quiz-container">
                <div className="loading-spinner">Loading quiz...</div>
            </div>
        );
    }

    if (error || !quizData) {
        return (
            <div className="shared-quiz-container">
                <div className="error-message">{error || 'Quiz not found'}</div>
            </div>
        );
    }

    // Show completion screen if quiz is completed
    if (quizCompleted) {
        const gradeInfo = getGradeMessage(score, quizData.questions.length);
        const percentage = Math.round((score / quizData.questions.length) * 100);
        
        return (
            <div className="shared-quiz-container">
                <header className="shared-quiz-header">
                    <div className="header-left">
                        <div className="logo">
                            <div className="logo-icon"><img src="https://iili.io/KYpg7rg.png" alt="Study-Space Logo" className='take-quiz-logo-icon-img' /></div>
                            <span className="logo-text">StudySpace</span>
                        </div>
                    </div>
                </header>

                <main className="shared-quiz-main">
                    <div className="quiz-completion">
                        <div className="completion-card">
                            <div className="completion-header">
                                <h1>Quiz Completed!</h1>
                                <div className="score-display">
                                    <div className="score-circle" style={{ borderColor: gradeInfo.color }}>
                                        <span className="score-number">{score}</span>
                                        <span className="score-total">/{quizData.questions.length}</span>
                                    </div>
                                    <div className="score-percentage" style={{ color: gradeInfo.color }}>
                                        {percentage}%
                                    </div>
                                </div>
                            </div>
                            
                            <div className="grade-message" style={{ color: gradeInfo.color }}>
                                <span className="grade-emoji">{gradeInfo.emoji}</span>
                                <span className="grade-text">{gradeInfo.message}</span>
                            </div>
                            
                            <div className="completion-actions">
                                <button 
                                    className="retake-btn"
                                    onClick={() => {
                                        setQuizCompleted(false);
                                        setCurrentQuestionIndex(0);
                                        setSelectedAnswers({});
                                        setAnswerFeedback({});
                                        setShowExplanations({});
                                        setScore(0);
                                    }}
                                >
                                    Retake Quiz
                                </button>

                            </div>

                           
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    const currentQuestion = quizData.questions[currentQuestionIndex];
    const isAnswerSelected = selectedAnswers[currentQuestion.id] !== undefined;
    const showExplanation = showExplanations[currentQuestion.id];
    const feedback = answerFeedback[currentQuestion.id];

    return (
        <div className="shared-quiz-container">
            {/* Header */}
            <header className="shared-quiz-header">
                <div className="header-left">
                    <a href="/" className="logo">
                    <div className="logo-icon">S</div>
                        <span className="logo-text">StudySpace</span>
                    </a>
                </div>
                <div className="header-right">
                    <button className="header-btn secondary">
                        <ArrowLeft className="btn-icon" />
                        See all Notes
                    </button>
                    <button onClick={signUpModal} className="header-btn primary">
                        <Plus className="btn-icon" />
                        Get Started
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="shared-quiz-main">
                <div className="quiz-content">
                    {/* Left Column - Questions */}
                    <div className="questions-column">
                        <div className="quiz-title">
                            <FileText className="title-icon" />
                            <h1>Quiz questions</h1>
                        </div>

                        <div className="question-container">
                            <div className="question-header">
                                <div className="question-label">
                                    <Star className="star-icon" />
                                    Question {currentQuestionIndex + 1} of {quizData.questions.length}
                                </div>
                            </div>

                            <div className="question-text">
                                {currentQuestion.question}
                            </div>

                            <div className="answer-options">
                                {currentQuestion.options.map((option, index) => {
                                    let optionClass = 'answer-option';
                                    let icon = null;
                                    
                                    if (feedback) {
                                        if (index === feedback.correct) {
                                            // Correct answer - always show green
                                            optionClass += ' correct-answer';
                                            icon = <Check className="option-icon correct-icon" />;
                                        } else if (index === feedback.selected && !feedback.isCorrect) {
                                            // Selected wrong answer - show red
                                            optionClass += ' incorrect-answer';
                                            icon = <X className="option-icon incorrect-icon" />;
                                        } else {
                                            // Unselected options
                                            optionClass += ' unselected';
                                        }
                                    } else if (selectedAnswers[currentQuestion.id] === index) {
                                        // Currently selected but no feedback yet
                                        optionClass += ' selected';
                                    }
                                    
                                    return (
                                        <button
                                            key={index}
                                            className={optionClass}
                                            onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                                            disabled={feedback !== undefined}
                                        >
                                            {icon || (
                                                <span className="option-letter">
                                                    {String.fromCharCode(65 + index)}
                                                </span>
                                            )}
                                            <span className="option-text">{option}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Answer Feedback */}
                            {feedback && (
                                <div className={`answer-feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
                                    {feedback.isCorrect ? (
                                        <>
                                            <Check className="feedback-icon" />
                                            <span>Correct!</span>
                                        </>
                                    ) : (
                                        <>
                                            <X className="feedback-icon" />
                                            <span>Incorrect</span>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Navigation */}
                            <div className="question-navigation">
                                <button 
                                    className="nav-btn prev"
                                    onClick={handlePrevQuestion}
                                    disabled={currentQuestionIndex === 0}
                                >
                                    Previous
                                </button>
                                <button 
                                    className="nav-btn next"
                                    onClick={handleNextQuestion}
                                    disabled={!isAnswerSelected}
                                >
                                    {currentQuestionIndex === quizData.questions.length - 1 ? 'Finish Quiz' : 'Next'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Explanation */}
                    <div className="explanation-column">
                        <div className="explanation-header">
                            <MessageCircle className="explanation-icon" />
                            <h2>Explanation</h2>
                        </div>

                        <div className="explanation-content">
                            {showExplanation ? (
                                <div className="explanation-text">
                                    {currentQuestion.explanation}
                                </div>
                            ) : (
                                <div className="explanation-placeholder">
                                    Select an answer to see the explanation
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {showSignUpModal && (
        <div className="modal">
            <div className="modal-content">
                <button className="modal-close" onClick={() => setShowSignUpModal(false)}>×</button>
                
                <h2 className="modal-title">Welcome to StudySpace</h2>
                    
                <button onClick={handleSignInWithGoogle} className="google-btn" disabled={authLoading}>
                    <div className="google-logo">G</div>
                    {authLoading ? 'Signing in...' : 'Continue with Google'}
                </button>
                
                {authError && (
                    <div className="auth-error">
                        {authError}
                    </div>
                )}
                
                <div className="separator">
                    <span>or</span>
                </div>
                
                <input type="email" className="email-input" placeholder="EMAIL" />
                
                <button className="email-btn">Continue with email</button>
                
                <p className="login-link">
                    Have an Account? <a href="#" onClick={() => setShowSignUpModal(true)}>Log In</a>
                </p>
            </div>
        </div>
    )}
            </main>
        </div>
    );
};

export default SharedQuiz;